import {
  FormContext,
  FormContextProvider,
  FormSchemaFields,
} from '@stormwater-software/lib-form';
import { UIControlType } from '@stormwater-software/lib-form-control-types';
import { Modal } from '@stormwater-software/lib-ui';
import classNames from 'classnames';
import React, { useState } from 'react';
import { useQueryClient } from 'react-query';
import {
  DragDropContext,
  Draggable,
  DraggableProvided,
  Droppable,
  DropResult,
} from 'react-beautiful-dnd';
import { toast } from 'react-toastify';
import {
  AssetType,
  AssetTypeField,
  assetTypesApi,
} from '../../utils/api/asset';
import AssetOptionsTagInput from './AssetOptionsTagInput';
import CopyToDivisionModal from './CopyToDivisionModal';
import {
  ASSET_COLORS,
  buildAssetFeatureableData,
  symbolCodeLabel,
} from '../../utils/assets';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faGripVertical, faPlus } from '@fortawesome/free-solid-svg-icons';

const fieldTypeOptions = [
  { label: 'Text', value: 'text' },
  { label: 'Number', value: 'number' },
  { label: 'Select / Enum', value: 'select' },
  { label: 'Date', value: 'date' },
  { label: 'Yes / No', value: 'yes_no' },
  { label: 'Yes / No / N/A', value: 'yes_no_na' },
];

const detailsSchema = (canHandleEdit: boolean) => {
  return {
    name: {
      label: 'Name *',
      validation: { required: true },
      disabled: !canHandleEdit,
    },
    code: {
      label: 'Code *',
      placeholder: 'eg. AST, IBU',
      validation: { required: true, maxLength: 12 },
      disabled: !canHandleEdit,
    },
    description: {
      label: 'Description',
      controlType: UIControlType.textarea,
      disabled: !canHandleEdit,
    },
  };
};

const fieldCoreSchema = (canHandleEdit: boolean) => {
  return {
    label: {
      label: 'Label *',
      validation: { required: true },
      disabled: !canHandleEdit,
    },
    field_type: {
      label: 'Type *',
      controlType: UIControlType.select,
      options: fieldTypeOptions,
      valueKey: 'value',
      labelKey: 'label',
      validation: { required: true },
      disabled: !canHandleEdit,
    },
  };
};

const fieldUnitSchema = (canHandleEdit: boolean) => {
  return {
    unit: { label: 'Unit', disabled: !canHandleEdit },
  };
};

interface IAssetTypeEditModalProps {
  divisionId: number;
  assetType?: AssetType | null;
  length: number;
  onSaved: () => void;
  onClose: () => void;
  onDelete?: () => void;
}

const AssetTypeEditModal: React.FC<IAssetTypeEditModalProps> = ({
  divisionId,
  assetType,
  length,
  onSaved,
  onClose,
  onDelete,
}) => {
  const isEdit = !!assetType;
  const assetTypeId = assetType?.id;
  const queryClient = useQueryClient();
  const refreshAssetTypeCaches = () =>
    queryClient.invalidateQueries(['assetTypes', divisionId]);
  const [tab, setTab] = useState<'details' | 'fields'>('details');
  const [showCopyModal, setShowCopyModal] = useState(false);
  const [editingField, setEditingField] =
    useState<Partial<AssetTypeField> | null>(null);
  const [fields, setFields] = useState<AssetTypeField[]>(
    assetType?.fields || [],
  );
  const canHandleEdit = isEdit ? assetType && !assetType.is_system : true;

  const handleSaveDetails = async (value: any) => {
    const { color, ...details } = value;
    const data = {
      ...details,
      display_order: isEdit ? assetType!.display_order : length,
      featureable_data: buildAssetFeatureableData(color, details.code),
    };

    try {
      if (assetTypeId) {
        await assetTypesApi.update(assetTypeId, data);
      } else {
        await assetTypesApi.create(divisionId, data);
      }
      toast.success(`Asset type ${isEdit ? 'updated' : 'created'}`);
      onSaved();
      onClose();
    } catch {
      toast.error('Failed to save asset type. Please try again.');
    }
  };

  const handleSaveField = async (value: any) => {
    if (!assetTypeId) return;

    const { unit, options, ...rest } = value;
    const payload = {
      ...editingField,
      ...rest,
      display_order: editingField?.display_order ?? fields.length,
      metadata: {
        ...(unit ? { unit } : {}),
        ...(rest.field_type === 'select' && Array.isArray(options)
          ? { options: options.map((o: string) => o.trim()).filter(Boolean) }
          : {}),
      },
    };

    try {
      const saved = payload.id
        ? await assetTypesApi.fields.update(assetTypeId, payload.id, payload)
        : await assetTypesApi.fields.create(assetTypeId, payload);

      setFields((prev) =>
        payload.id
          ? prev.map((f) => (f.id === saved.id ? saved : f))
          : [...prev, saved],
      );
      refreshAssetTypeCaches();
      toast.success('Field saved');
      setEditingField(null);
    } catch {
      toast.error('Failed to save field. Please try again.');
    }
  };

  const handleDragEnd = async (result: DropResult) => {
    if (
      !assetTypeId ||
      !result.destination ||
      result.destination.index === result.source.index
    )
      return;

    const reordered = [...fields];
    const [moved] = reordered.splice(result.source.index, 1);

    reordered.splice(result.destination.index, 0, moved);

    const withOrder = reordered.map((f, i) => ({ ...f, display_order: i }));

    setFields(withOrder);
    try {
      await assetTypesApi.fields.reorder(
        assetTypeId,
        withOrder.map((f) => ({ id: f.id!, display_order: f.display_order })),
      );
      refreshAssetTypeCaches();
      toast.success('Field order updated');
    } catch {
      toast.error('Failed to save field order.');
    }
  };

  const handleDeleteField = async (fieldId: number) => {
    if (!assetTypeId) return;
    try {
      await assetTypesApi.fields.destroy(assetTypeId, fieldId);
      setFields((prev) => prev.filter((f) => f.id !== fieldId));
      refreshAssetTypeCaches();
      toast.success('Field removed');
    } catch {
      toast.error('Failed to remove field. Please try again.');
    }
  };

  const renderFieldRow = (
    field: AssetTypeField,
    drag: DraggableProvided,
    isClone: boolean,
  ) => {
    const row = (
      <tr
        ref={drag.innerRef}
        {...drag.draggableProps}
        style={drag.draggableProps.style}
      >
        <td
          className='asset-config__drag-handle'
          {...(canHandleEdit ? drag.dragHandleProps : {})}
        >
          {canHandleEdit && <FontAwesomeIcon icon={faGripVertical} />}
        </td>
        <td className='strong' data-label='Label'>
          {field.label}
        </td>
        <td data-label='Type'>
          <span className='asset-config__badge-soft is-teal'>
            {field.field_type.replace(/_/g, ' ')}
          </span>
        </td>
        <td data-label='Scope'>
          <span className='asset-config__badge-soft'>
            {field.is_inspectable ? 'Inspectable' : 'Static'}
          </span>
        </td>
        <td data-label='Required'>
          {field.is_required ? (
            'Yes'
          ) : (
            <span className='asset-config__muted'>—</span>
          )}
        </td>
        <td data-label='Active'>
          <span
            className={classNames('asset-config__badge-soft', {
              'is-green': field.is_active,
            })}
          >
            {field.is_active ? 'Active' : 'Inactive'}
          </span>
        </td>
        <td className='asset-config__row-actions'>
          <>
            <button
              className='tiny outline'
              onClick={() =>
                setEditingField({
                  ...field,
                  ...(field.metadata?.unit
                    ? { unit: field.metadata.unit }
                    : {}),
                  ...(field.metadata?.options
                    ? { options: field.metadata.options }
                    : {}),
                } as any)
              }
            >
              {canHandleEdit ? 'Edit' : 'View'}
            </button>
            <button
              className='tiny outline warn'
              onClick={() => field.id && handleDeleteField(field.id)}
              disabled={!canHandleEdit || !field.id}
            >
              Delete
            </button>
          </>
        </td>
      </tr>
    );

    if (isClone) {
      return (
        <table className='asset-config__table asset-config__table--fields asset-config__table--clone'>
          <tbody>{row}</tbody>
        </table>
      );
    }

    return row;
  };

  return (
    <Modal
      title={
        isEdit
          ? canHandleEdit
            ? `Edit: ${assetType!.name}`
            : `View ${assetType!.name}`
          : 'New Asset Type'
      }
      show
      hideButtons
      handleClose={onClose}
      classes='asset-type-modal'
    >
      {isEdit && (
        <div className='asset-type-modal__tabs-row'>
          <div className='asset-tabs'>
            <button
              className={classNames('asset-tabs__tab', {
                'is-active': tab === 'details',
              })}
              onClick={() => setTab('details')}
            >
              Details
            </button>
            <button
              className={classNames('asset-tabs__tab', {
                'is-active': tab === 'fields',
              })}
              onClick={() => setTab('fields')}
            >
              Fields
            </button>
          </div>
          {!assetType!.is_system && (
            <button
              className='outline asset-type-modal__copy'
              onClick={() => setShowCopyModal(true)}
            >
              Copy To Division
            </button>
          )}
        </div>
      )}

      {tab === 'details' && (
        <FormContextProvider
          initialValue={{
            name: assetType?.name || '',
            code: assetType?.code || '',
            description: assetType?.description || '',
            color:
              assetType?.featureable_data?.config?.color || ASSET_COLORS[0],
          }}
        >
          <FormContext.Consumer>
            {(formContext) => (
              <div className='asset-type-details'>
                <FormSchemaFields
                  schema={detailsSchema(canHandleEdit!)}
                  formData={formContext.value}
                  onChange={formContext.set}
                />
                <label className='asset-config__label'>Map Color</label>
                <div className='asset-symbol-editor'>
                  <span
                    className='asset-symbol-preview'
                    style={{
                      backgroundColor: formContext.value.color || '#9CA3AF',
                    }}
                  >
                    <span className='asset-symbol-preview__code'>
                      {symbolCodeLabel(formContext.value.code) || 'AST'}
                    </span>
                  </span>
                  <div className='asset-symbol-editor__pickers'>
                    <div className='asset-color-swatches'>
                      {ASSET_COLORS.map((assetColor) => (
                        <button
                          key={assetColor}
                          type='button'
                          aria-label={`Select ${assetColor}`}
                          className={classNames(
                            'asset-color-swatches__swatch',
                            {
                              'is-selected':
                                formContext.value.color === assetColor,
                            },
                          )}
                          style={
                            {
                              backgroundColor: assetColor,
                              '--swatch-color': assetColor,
                            } as React.CSSProperties
                          }
                          onClick={() => formContext.set('color', assetColor)}
                          disabled={!canHandleEdit}
                        />
                      ))}
                    </div>
                  </div>
                </div>

                <div className='asset-type-modal__footer'>
                  {isEdit && onDelete && (
                    <button
                      className='outline warn asset-config__delete'
                      onClick={onDelete}
                      disabled={!canHandleEdit}
                    >
                      Delete Asset Type
                    </button>
                  )}
                  <span className='spacer' />
                  <button className='outline' onClick={onClose}>
                    Cancel
                  </button>
                  <button
                    className='primary'
                    onClick={() => handleSaveDetails(formContext.value)}
                    disabled={!canHandleEdit}
                  >
                    Save Changes
                  </button>
                </div>
              </div>
            )}
          </FormContext.Consumer>
        </FormContextProvider>
      )}

      {tab === 'fields' && isEdit && (
        <div className='asset-type-fields'>
          <div className='asset-type-fields__toolbar'>
            <button
              className='asset-config__add'
              onClick={() =>
                setEditingField({ field_type: 'text', is_inspectable: false })
              }
              disabled={!canHandleEdit}
            >
              <FontAwesomeIcon icon={faPlus} />&nbsp; Add Field
            </button>
          </div>

          {editingField && (
            <FormContextProvider initialValue={editingField}>
              <FormContext.Consumer>
                {(formContext) => (
                  <div className='asset-config__editor'>
                    <h4>{editingField.id ? 'Edit Field' : 'Add Field'}</h4>

                    <label className='asset-config__label'>Field Scope *</label>
                    <div className='asset-scope'>
                      {[
                        {
                          v: false,
                          t: 'Static',
                          d: 'Set once — capacity, serial number, tank material',
                        },
                        {
                          v: true,
                          t: 'Inspectable',
                          d: 'Checked each inspection — condition, labels, containment',
                        },
                      ].map((opt) => (
                        <button
                          key={opt.t}
                          type='button'
                          className={classNames('asset-scope__card', {
                            'is-selected':
                              !!formContext.value.is_inspectable === opt.v,
                          })}
                          onClick={() =>
                            formContext.set('is_inspectable', opt.v)
                          }
                          disabled={!canHandleEdit}
                        >
                          <strong>{opt.t}</strong>
                          <span>{opt.d}</span>
                        </button>
                      ))}
                    </div>

                    <FormSchemaFields
                      schema={fieldCoreSchema(canHandleEdit!)}
                      formData={formContext.value}
                      onChange={formContext.set}
                    />

                    {formContext.value.field_type === 'select' && (
                      <div className='asset-config__options'>
                        <label className='asset-config__label'>Options</label>
                        <AssetOptionsTagInput
                          value={formContext.value.options || []}
                          onChange={(opts) => formContext.set('options', opts)}
                          canHandleEdit={canHandleEdit!}
                        />
                        <span className='asset-config__hint'>
                          Press Enter after each option to add it as a tag.
                        </span>
                      </div>
                    )}

                    {formContext.value.field_type === 'number' && (
                      <FormSchemaFields
                        schema={fieldUnitSchema(canHandleEdit!)}
                        formData={formContext.value}
                        onChange={formContext.set}
                      />
                    )}

                    <div className='asset-config__inline-checks'>
                      <label className='asset-config__checkbox'>
                        <input
                          type='checkbox'
                          checked={!!formContext.value.is_required}
                          onChange={(e) =>
                            formContext.set('is_required', e.target.checked)
                          }
                          disabled={!canHandleEdit}
                        />
                        Required
                      </label>
                      <label className='asset-config__checkbox'>
                        <input
                          type='checkbox'
                          checked={formContext.value.is_active !== false}
                          onChange={(e) =>
                            formContext.set('is_active', e.target.checked)
                          }
                          disabled={!canHandleEdit}
                        />
                        Active
                      </label>
                    </div>

                    <div className='asset-config__editor-actions'>
                      <button
                        className='primary'
                        onClick={() => handleSaveField(formContext.value)}
                        disabled={!canHandleEdit}
                      >
                        Save Changes
                      </button>
                      <button
                        className='outline'
                        onClick={() => setEditingField(null)}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </FormContext.Consumer>
            </FormContextProvider>
          )}

          <table className='asset-config__table asset-config__table--fields'>
            <thead>
              <tr>
                <th aria-label='drag' />
                <th>Label</th>
                <th>Type</th>
                <th>Scope</th>
                <th>Required</th>
                <th>Active</th>
                <th aria-label='actions' />
              </tr>
            </thead>
            <DragDropContext onDragEnd={handleDragEnd}>
              <Droppable
                droppableId='asset-type-fields'
                renderClone={(provided, _snapshot, rubric) =>
                  renderFieldRow(fields[rubric.source.index], provided, true)
                }
              >
                {(provided) => (
                  <tbody ref={provided.innerRef} {...provided.droppableProps}>
                    {fields.map((field, index) => (
                      <Draggable
                        key={field.id}
                        draggableId={String(field.id)}
                        index={index}
                      >
                        {(drag) => renderFieldRow(field, drag, false)}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                    {fields.length === 0 && (
                      <tr>
                        <td colSpan={7} className='asset-config__empty-cell'>
                          No fields yet — add the first field to capture asset
                          details.
                        </td>
                      </tr>
                    )}
                  </tbody>
                )}
              </Droppable>
            </DragDropContext>
          </table>
        </div>
      )}

      {showCopyModal && assetTypeId && (
        <CopyToDivisionModal
          title='Copy Asset Type to Division'
          successMessage='Asset type copied'
          onCopy={(divisionIds) =>
            assetTypesApi.copy(divisionIds, [assetTypeId])
          }
          onClose={() => setShowCopyModal(false)}
        />
      )}
    </Modal>
  );
};

export default AssetTypeEditModal;
